import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BlobType, FileModel, UploadFileItem, UserInfo, UserUpdate } from '@ca/core/models';
import { UserNotificationService, UserService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, catchError, tap } from 'rxjs';
import { SUPPORTED_IMAGE_TYPES } from '../../../../shared/constants/form-validators';

@Component({
    selector: 'ca-portrait-editor',
    templateUrl: './portrait-editor.component.html',
    styleUrls: ['./portrait-editor.component.scss'],
})
export class PortraitEditorComponent implements OnInit {
    @Input() userInfo?: UserInfo;

    isSubmitting: boolean = false;
    supportedImageTypes = SUPPORTED_IMAGE_TYPES;
    portraitBlobType: BlobType = BlobType.ProfileImage;

    formGroup: UntypedFormGroup;

    constructor(
        private fb: UntypedFormBuilder,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.buildImageForm();
    }

    buildImageForm(): void {
        this.formGroup = this.fb.group({
            picture: [this.userInfo?.picture],
        });
    }

    onUploadCompleted({ token }: FileModel): void {
        this.formGroup.get('picture')?.setValue(token);
        this.formGroup.get('picture')?.markAsDirty({ onlySelf: true });
    }

    submitProfile(): void {
        if (this.formGroup.valid && this.userInfo) {
            const { title, firstName, lastName, email, organization, occupation, phoneNumber, website, country, city, description, headerImage } =
                this.userInfo;

            this.isSubmitting = true;
            const payload: UserUpdate = {
                title,
                firstName,
                lastName,
                email,
                organization,
                occupation,
                phoneNumber,
                website,
                country,
                city,
                description,
                headerImage,
                picture: this.formGroup.value.picture,
            };

            this.userNotificationService.showSpinner();

            this.userService
                .updateUserProfile$(payload)
                .pipe(
                    tap((userInfo: UserInfo) => {
                        this.userNotificationService.hideSpinner();
                        this.userNotificationService.showSuccess('GENERAL.CHANGED_SUCCESS');
                        this.activeModal.close(userInfo);
                    }),
                    catchError((err: unknown) => {
                        this.userNotificationService.hideSpinner();
                        console.error('Failed to save your changes.', err);
                        this.isSubmitting = false;
                        this.userNotificationService.showError('GENERAL.CHANGED_FAILURE');
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    removeFileItem(): void {
        this.formGroup.get('picture')?.setValue(null);
        this.formGroup.get('picture')?.markAsDirty({ onlySelf: true });
    }

    setPictureError(invalidFiles: UploadFileItem[]): void {
        this.formGroup.get('picture')?.setValue('');
        this.formGroup.get('picture')?.markAsDirty({ onlySelf: true });

        if (invalidFiles[0].unsupportedFileTypeError) {
            this.formGroup.get('picture')?.setErrors({ invalidImageType: 'Invalid image type.' });
        }
        if (invalidFiles[0].maxFileSizeError) {
            this.formGroup.get('picture')?.setErrors({ maxFileSize: 'Image size cannot be larger than 1MB.' });
        }
    }

    dismiss(reason: string): void {
        this.activeModal.dismiss(reason);
    }
}
